import React, { Suspense, lazy } from 'react';
import { withRouter, Switch, Redirect, Route } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { ProtectedAuthorizedRoute } from './ProtectedAuthorizedRoute';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

/* loader component for Suspense*/
import PageLoader from './components/Common/PageLoader';

import Base from './components/Layout/Base';
import BasePage from './components/Layout/BasePage';
// import BaseHorizontal from './components/Layout/BaseHorizontal';

/* Used to render a lazy component with react-router */
const waitFor = Tag => props => <Tag {...props}/>;

const MainDashboard = lazy(() => import('./components/Dashboard/MainDashboard'));
const IntraDay1Dashboard = lazy(() => import('./components/Dashboard/Configurations/IntraDay1Dashboard'));
const IntraDay2Dashboard = lazy(() => import('./components/Dashboard/Configurations/IntraDay2Dashboard'));
const IntraDay3Dashboard = lazy(() => import('./components/Dashboard/Configurations/IntraDay3Dashboard'));
const IntraDay4Dashboard = lazy(() => import('./components/Dashboard/Configurations/IntraDay4Dashboard'));
const ShortTermDashboard = lazy(() => import('./components/Dashboard/Configurations/ShortTermDashboard'));
const MidTermDashboard = lazy(() => import('./components/Dashboard/Configurations/MidTermDashboard'));
const LongTerm1Dashboard = lazy(() => import('./components/Dashboard/Configurations/LongTerm1Dashboard'));
const LongTerm2Dashboard = lazy(() => import('./components/Dashboard/Configurations/LongTerm2Dashboard'));
const LongTerm3Dashboard = lazy(() => import('./components/Dashboard/Configurations/LongTerm3Dashboard'));
const IntraDayShortTermDashboard = lazy(() => import('./components/Dashboard/Configurations/IntraDayShortTermDashboard'));
const FourHourSignalsDashboard = lazy(() => import('./components/Dashboard/Configurations/FourHourSignalsDashboard'));
const TwelveHourSignalsDashboard = lazy(() => import('./components/Dashboard/Configurations/TwelveHourSignalsDashboard'));
const EighteenHourSignalsDashboard = lazy(() => import('./components/Dashboard/Configurations/EighteenHourSignalsDashboard'));
const DailySignalsDashboard = lazy(() => import('./components/Dashboard/Configurations/DailySignalsDashboard'));
const WeeklySignalsDashboard = lazy(() => import('./components/Dashboard/Configurations/WeeklySignalsDashboard'));
const FourteenDaySignalsDashboard = lazy(() => import('./components/Dashboard/Configurations/FourteenDaySignalsDashboard'));
const MonthlyDashboard = lazy(() => import('./components/Dashboard/Configurations/MonthlyDashboard'));
const MidTerm1Dashboard = lazy(() => import('./components/Dashboard/Configurations/MidTerm1Dashboard'));
const MidTerm2Dashboard = lazy(() => import('./components/Dashboard/Configurations/MidTerm2Dashboard'));
const LongTermSecondDashboard = lazy(() => import('./components/Dashboard/Configurations/LongTermSecondDashboard'));
const DefaultRerouteComponent = lazy(() => import('./components/Dashboard/DefaultReroute'));

const Widgets = lazy(() => import('./components/Widgets/Widgets'));

const Buttons = lazy(() => import('./components/Elements/Buttons'));
const Notifications = lazy(() => import('./components/Elements/Notifications'));
const SweetAlert = lazy(() => import('./components/Elements/SweetAlert'));
const BsCarousel = lazy(() => import('./components/Elements/Carousel'));
const Spinner = lazy(() => import('./components/Elements/Spinner'));
const DropdownAnimation = lazy(() => import('./components/Elements/DropdownAnimation'));
const Nestable = lazy(() => import('./components/Elements/Nestable'));
const Sortable = lazy(() => import('./components/Elements/Sortable'));
const Cards = lazy(() => import('./components/Elements/Cards'));
const Grid = lazy(() => import('./components/Elements/Grid'));
const GridMasonry = lazy(() => import('./components/Elements/GridMasonry'));
const Typography = lazy(() => import('./components/Elements/Typography'));
const FontIcons = lazy(() => import('./components/Elements/FontIcons'));
const WeatherIcons = lazy(() => import('./components/Elements/WeatherIcons'));
const Colors = lazy(() => import('./components/Elements/Colors'));

const MapsGoogle = lazy(() => import('./components/Maps/MapsGoogle'));
const MapsVector = lazy(() => import('./components/Maps/MapsVector'));

const TableStandard = lazy(() => import('./components/Tables/TableStandard'));
const TableExtended = lazy(() => import('./components/Tables/TableExtended'));
const Datatable = lazy(() => import('./components/Tables/DatatableView'));
const DataGrid = lazy(() => import('./components/Tables/DataGrid'));

const FormStandard = lazy(() => import('./components/Forms/FormStandard'));
const FormExtended = lazy(() => import('./components/Forms/FormExtended'));
const FormValidation = lazy(() => import('./components/Forms/FormValidation'));
const FormWizard = lazy(() => import('./components/Forms/FormWizard'));
const FormUpload = lazy(() => import('./components/Forms/FormUpload'));
const FormCropper = lazy(() => import('./components/Forms/FormCropper'));

const Login = lazy(() => import('./components/Pages/Login'));
const Logout = lazy(() => import('./components/Pages/Logout'));
const Register = lazy(() => import('./components/Pages/Register'));
const Recover = lazy(() => import('./components/Pages/Recover'));
const Lock = lazy(() => import('./components/Pages/Lock'));
const NotFound = lazy(() => import('./components/Pages/NotFound'));
const Error500 = lazy(() => import('./components/Pages/Error500'));
const Maintenance = lazy(() => import('./components/Pages/Maintenance'));

const Mailbox = lazy(() => import('./components/Extras/Mailbox'));
const Timeline = lazy(() => import('./components/Extras/Timeline'));
const Calendar = lazy(() => import('./components/Extras/Calendar'));
const Invoice = lazy(() => import('./components/Extras/Invoice'));
const Search = lazy(() => import('./components/Extras/Search'));
const Todo = lazy(() => import('./components/Extras/Todo'));
const Profile = lazy(() => import('./components/Extras/Profile'));
const BugTracker = lazy(() => import('./components/Extras/BugTracker'));
const ContactDetails = lazy(() => import('./components/Extras/ContactDetails'));
const Contacts = lazy(() => import('./components/Extras/Contacts'));
const Faq = lazy(() => import('./components/Extras/Faq'));
const FileManager = lazy(() => import('./components/Extras/FileManager'));
const Followers = lazy(() => import('./components/Extras/Followers'));
const HelpCenter = lazy(() => import('./components/Extras/HelpCenter'));
const Plans = lazy(() => import('./components/Extras/Plans'));
const ProjectDetails = lazy(() => import('./components/Extras/ProjectDetails'));
const Projects = lazy(() => import('./components/Extras/Projects'));
const Settings = lazy(() => import('./components/Extras/Settings'));
const SocialBoard = lazy(() => import('./components/Extras/SocialBoard'));
const TeamViewer = lazy(() => import('./components/Extras/TeamViewer'));
const VoteLinks = lazy(() => import('./components/Extras/VoteLinks'));

const EcommerceOrder = lazy(() => import('./components/Ecommerce/EcommerceOrders'));
const EcommerceOrderView = lazy(() => import('./components/Ecommerce/EcommerceOrderView'));
const EcommerceProduct = lazy(() => import('./components/Ecommerce/EcommerceProducts'));
const EcommerceProductView = lazy(() => import('./components/Ecommerce/EcommerceProductView'));
const EcommerceCheckout = lazy(() => import('./components/Ecommerce/EcommerceCheckout'));

const BlogList = lazy(() => import('./components/Blog/BlogList'));
const BlogPost = lazy(() => import('./components/Blog/BlogPost'));
const BlogArticle = lazy(() => import('./components/Blog/BlogArticles'));
const BlogArticleView = lazy(() => import('./components/Blog/BlogArticleView'));

const ForumHome = lazy(() => import('./components/Forum/ForumHome'));


// List of routes that uses the page layout
// listed here to Switch between layouts
// depending on the current pathname
const listofPages = [
    '/login',
    '/logout',
    '/register',
    '/recover',
    '/lock',
    '/notfound',
    '/error500',
    '/maintenance'
];

const Routes = ({ location }) => {
    const currentKey = location.pathname.split('/')[1] || '/';
    const timeout = { enter: 500, exit: 500 };

    // Animations supported
    //      'rag-fadeIn'
    //      'rag-fadeInRight'
    //      'rag-fadeInLeft'

    const animationName = 'rag-fadeIn'

    if(listofPages.indexOf(location.pathname) > -1) {
        return (
            // Page Layout component wrapper
            <BasePage>
                <Suspense fallback={<PageLoader/>}>
                    <Switch location={location}>
                        <Route path="/login" component={waitFor(Login)}/>
                        <Route path="/logout" component={waitFor(Logout)}/>
                        <Route path="/register" component={waitFor(Register)}/>
                        <Route path="/recover" component={waitFor(Recover)}/>
                        <Route path="/lock" component={waitFor(Lock)}/>
                        <Route path="/notfound" component={waitFor(NotFound)}/>
                        <Route path="/error500" component={waitFor(Error500)}/>
                        <Route path="/maintenance" component={waitFor(Maintenance)}/>
                    </Switch>
                </Suspense>
            </BasePage>
        )
    }
    else {
        return (
            // Layout component wrapper
            // Use <BaseHorizontal> to change layout
            <Base>
              <TransitionGroup>
                <CSSTransition key={currentKey} timeout={timeout} classNames={animationName} exit={false}>
                    <div>
                        <Suspense fallback={<PageLoader/>}>
                            <Switch location={location}>

                                {/*Dashboard*/}
                                <ProtectedAuthorizedRoute path="/Main" component={waitFor(MainDashboard)}/>
                                <ProtectedAuthorizedRoute path="/1Minute-30Minutes" component={waitFor(IntraDay1Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/45Minutes-8Hours" component={waitFor(IntraDay2Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/9Hours-18Hours" component={waitFor(IntraDay3Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/19Hours-3Days" component={waitFor(IntraDay4Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/4Days-12Days" component={waitFor(ShortTermDashboard)}/>
                                <ProtectedAuthorizedRoute path="/13Days-22Days" component={waitFor(MidTermDashboard)}/>
                                <ProtectedAuthorizedRoute path="/23Days-1-and-a-half-Months" component={waitFor(LongTerm1Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/2Months-10Months" component={waitFor(LongTerm2Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/11Months-2Years" component={waitFor(LongTerm3Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/intraday-1" component={waitFor(IntraDayShortTermDashboard)}/>
                                <ProtectedAuthorizedRoute path="/intraday-2" component={waitFor(FourHourSignalsDashboard)}/>
                                <ProtectedAuthorizedRoute path="/daily-1" component={waitFor(TwelveHourSignalsDashboard)}/>
                                <ProtectedAuthorizedRoute path="/daily-2" component={waitFor(DailySignalsDashboard)}/>
                                <ProtectedAuthorizedRoute path="/2-day" component={waitFor(EighteenHourSignalsDashboard)}/>
                                <ProtectedAuthorizedRoute path="/weekly" component={waitFor(WeeklySignalsDashboard)}/>
                                <ProtectedAuthorizedRoute path="/monthly-1" component={waitFor(FourteenDaySignalsDashboard)}/>
                                <ProtectedAuthorizedRoute path="/monthly-2" component={waitFor(MonthlyDashboard)}/>
                                <ProtectedAuthorizedRoute path="/mid-term-1" component={waitFor(MidTerm1Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/mid-term-2" component={waitFor(MidTerm2Dashboard)}/>
                                <ProtectedAuthorizedRoute path="/long-term" component={waitFor(LongTermSecondDashboard)}/>

                                {/*Widgets*/}
                                <ProtectedRoute path="/widgets" component={waitFor(Widgets)}/>

                                {/*Elements*/}
                                <ProtectedRoute path="/buttons" component={waitFor(Buttons)}/>
                                <ProtectedRoute path="/notifications" component={waitFor(Notifications)}/>
                                <ProtectedRoute path="/sweetalert" component={waitFor(SweetAlert)}/>
                                <ProtectedRoute path="/carousel" component={waitFor(BsCarousel)}/>
                                <ProtectedRoute path="/spinners" component={waitFor(Spinner)}/>
                                <ProtectedRoute path="/dropdown" component={waitFor(DropdownAnimation)}/>
                                <ProtectedRoute path="/nestable" component={waitFor(Nestable)}/>
                                <ProtectedRoute path="/sortable" component={waitFor(Sortable)}/>
                                <ProtectedRoute path="/cards" component={waitFor(Cards)}/>
                                <ProtectedRoute path="/grid" component={waitFor(Grid)}/>
                                <ProtectedRoute path="/grid-masonry" component={waitFor(GridMasonry)}/>
                                <ProtectedRoute path="/typography" component={waitFor(Typography)}/>
                                <ProtectedRoute path="/icons-font" component={waitFor(FontIcons)}/>
                                <ProtectedRoute path="/icons-weather" component={waitFor(WeatherIcons)}/>
                                <ProtectedRoute path="/colors" component={waitFor(Colors)}/>

                                {/*Forms*/}
                                <ProtectedRoute path="/form-standard" component={waitFor(FormStandard)}/>
                                <ProtectedRoute path="/form-extended" component={waitFor(FormExtended)}/>
                                <ProtectedRoute path="/form-validation" component={waitFor(FormValidation)}/>
                                <ProtectedRoute path="/form-wizard" component={waitFor(FormWizard)}/>
                                <ProtectedRoute path="/form-upload" component={waitFor(FormUpload)}/>
                                <ProtectedRoute path="/form-cropper" component={waitFor(FormCropper)}/>

                                {/*Table*/}
                                <ProtectedRoute path="/table-standard" component={waitFor(TableStandard)}/>
                                <ProtectedRoute path="/table-extended" component={waitFor(TableExtended)}/>
                                <ProtectedRoute path="/table-datatable" component={waitFor(Datatable)}/>
                                <ProtectedRoute path="/table-datagrid" component={waitFor(DataGrid)}/>

                                {/*Maps*/}
                                <ProtectedRoute path="/map-google" component={waitFor(MapsGoogle)}/>
                                <ProtectedRoute path="/map-vector" component={waitFor(MapsVector)}/>

                                {/*Extras*/}
                                <ProtectedRoute path="/mailbox" component={waitFor(Mailbox)}/>
                                <ProtectedRoute path="/timeline" component={waitFor(Timeline)}/>
                                <ProtectedRoute path="/calendar" component={waitFor(Calendar)}/>
                                <ProtectedRoute path="/invoice" component={waitFor(Invoice)}/>
                                <ProtectedRoute path="/search" component={waitFor(Search)}/>
                                <ProtectedRoute path="/todo" component={waitFor(Todo)}/>
                                <ProtectedRoute path="/profile" component={waitFor(Profile)}/>
                                <ProtectedRoute path="/ecommerce-orders" component={waitFor(EcommerceOrder)}/>
                                <ProtectedRoute path="/ecommerce-order-view" component={waitFor(EcommerceOrderView)}/>
                                <ProtectedRoute path="/ecommerce-products" component={waitFor(EcommerceProduct)}/>
                                <ProtectedRoute path="/ecommerce-product-view" component={waitFor(EcommerceProductView)}/>
                                <ProtectedRoute path="/ecommerce-checkout" component={waitFor(EcommerceCheckout)}/>
                                <ProtectedRoute path="/blog-list" component={waitFor(BlogList)}/>
                                <ProtectedRoute path="/blog-post" component={waitFor(BlogPost)}/>
                                <ProtectedRoute path="/blog-articles" component={waitFor(BlogArticle)}/>
                                <ProtectedRoute path="/blog-article-view" component={waitFor(BlogArticleView)}/>
                                <ProtectedRoute path="/bug-tracker" component={waitFor(BugTracker)}/>
                                <ProtectedRoute path="/contact-details" component={waitFor(ContactDetails)}/>
                                <ProtectedRoute path="/contacts" component={waitFor(Contacts)}/>
                                <ProtectedRoute path="/faq" component={waitFor(Faq)}/>
                                <ProtectedRoute path="/file-manager" component={waitFor(FileManager)}/>
                                <ProtectedRoute path="/followers" component={waitFor(Followers)}/>
                                <ProtectedRoute path="/help-center" component={waitFor(HelpCenter)}/>
                                <ProtectedRoute path="/plans" component={waitFor(Plans)}/>
                                <ProtectedRoute path="/project-details" component={waitFor(ProjectDetails)}/>
                                <ProtectedRoute path="/projects" component={waitFor(Projects)}/>
                                <ProtectedRoute path="/settings" component={waitFor(Settings)}/>
                                <ProtectedRoute path="/social-board" component={waitFor(SocialBoard)}/>
                                <ProtectedRoute path="/team-viewer" component={waitFor(TeamViewer)}/>
                                <ProtectedRoute path="/vote-links" component={waitFor(VoteLinks)}/>

                                <ProtectedRoute path="/forum" component={waitFor(ForumHome)}/>

                                <ProtectedAuthorizedRoute path="/" component={waitFor(DefaultRerouteComponent)}/>

                                <Redirect to="/"/>
                            </Switch>
                        </Suspense>
                    </div>
                </CSSTransition>
              </TransitionGroup>
            </Base>
        )
    }
}

export default withRouter(Routes);